import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import type { Artist } from '../../types';
import { useLang } from '../../i18n/context';

export function ArtistsTab() {
  const { t, lang } = useLang();
  const [artists, setArtists] = useState<Artist[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);

  useEffect(() => { load(); }, [search]);

  async function load() {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      setArtists(await api.getArtists(params));
    } catch (err) {
      console.error('Failed to load artists:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id: string) {
    try {
      const data = await api.getArtist(id);
      setDetail(data);
      setSelectedId(id);
    } catch (err) {
      console.error('Failed to load detail:', err);
    }
  }

  const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-BE';

  const formatDate = (d: string) => {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const soundSystems = artists.filter(a => a.is_sound_system);
  const regularArtists = artists.filter(a => !a.is_sound_system);

  // Get event proximity color (same logic as map)
  function getEventDot(nextDate: string | null | undefined): { color: string; label: string; glow: boolean } | null {
    if (!nextDate) return null;
    const now = new Date();
    const eventDate = new Date(nextDate);
    const diffDays = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return null;
    if (diffDays <= 1) return { color: '#e53935', label: t('map.tomorrow').replace('!', ''), glow: true };
    if (diffDays <= 7) return { color: '#e53935', label: `${diffDays}j`, glow: false };
    if (diffDays <= 30) return { color: '#c9a84c', label: `${diffDays}j`, glow: false };
    return { color: '#4caf50', label: `${diffDays}j`, glow: false };
  }

  // Parse extra_urls JSON for Spotify/Tidal/Deezer badges
  function getExtraUrls(raw: any): Record<string, string> {
    if (!raw) return {};
    try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return {}; }
  }

  // Filter to upcoming events only
  const today = new Date().toISOString().split('T')[0];
  const upcomingEvents = (detail?.events || []).filter((e: any) => e.date >= today);

  return (
    <div>
      <div className="section-header">
        <span className="section-title">{t('artists.title')}</span>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {regularArtists.length} {t('artists.artists')} · {soundSystems.length} {t('artists.sound_systems')}
        </span>
      </div>

      <div className="filter-bar">
        <div className="search-wrapper">
          <span className="search-icon">🔍</span>
          <input
            className="search-input"
            placeholder={t('artists.search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading"><span className="spinner"></span> {t('common.loading')}</div>
      ) : (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1, maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
                  <th style={{ padding: '8px', color: 'var(--text-muted)', fontWeight: 600, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase' }}>{t('common.name')}</th>
                  <th style={{ padding: '8px', color: 'var(--text-muted)', fontWeight: 600, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase' }}>{t('common.type')}</th>
                  <th style={{ padding: '8px', color: 'var(--text-muted)', fontWeight: 600, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase' }}>{t('artists.genres')}</th>
                  <th style={{ padding: '8px', color: 'var(--text-muted)', fontWeight: 600, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase' }}>{t('artists.country')}</th>
                  <th style={{ padding: '8px', color: 'var(--text-muted)', fontWeight: 600, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase' }}>{t('artists.links')}</th>
                </tr>
              </thead>
              <tbody>
                {artists.map(a => {
                  const extra = getExtraUrls((a as any).extra_urls);
                  return (
                  <tr key={a.id}
                    style={{
                      borderBottom: '1px solid var(--border-color)',
                      cursor: 'pointer',
                      background: selectedId === a.id ? 'rgba(201,168,76,0.08)' : undefined,
                    }}
                    onClick={() => loadDetail(a.id)}
                  >
                    <td style={{ padding: '8px', fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {(() => {
                          const dot = getEventDot(a.next_event_date);
                          if (!dot) return null;
                          return (
                            <span
                              title={`${t('artists.next_event_in')} ${dot.label}`}
                              style={{
                                width: '8px', height: '8px', borderRadius: '50%',
                                backgroundColor: dot.color, flexShrink: 0,
                                boxShadow: dot.glow
                                  ? `0 0 6px ${dot.color}, 0 0 12px ${dot.color}`
                                  : `0 0 4px ${dot.color}80`,
                                animation: dot.glow ? 'pulse-dot 1.5s ease-in-out infinite' : undefined,
                              }}
                            />
                          );
                        })()}
                        <span>{a.name}</span>
                        {(a as any).validated === 'verified' && (
                          <span title="Verified on music platforms" style={{
                            fontSize: '10px', color: '#4caf50', marginLeft: '2px',
                          }}>✓</span>
                        )}
                        {!!a.has_sooncome && (
                          <span className="sooncome-badge" title="Soon Come">
                            🔥 SOON
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '8px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 700,
                        background: a.is_sound_system ? 'rgba(66,165,245,0.15)' : 'rgba(255,152,0,0.15)',
                        color: a.is_sound_system ? 'var(--blue)' : 'var(--orange)',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                      }}>
                        {a.is_sound_system ? 'Sound System' : t('artists.artist')}
                      </span>
                    </td>
                    <td style={{ padding: '8px' }}>
                      <div className="genre-tags" style={{ margin: 0 }}>
                        {a.genres?.slice(0, 3).map(g => (
                          <span key={g} className={`genre-tag ${g.replace(/\s+/g, '-')}`}>{g}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: '8px', color: 'var(--text-muted)' }}>{a.country || '-'}</td>
                    <td style={{ padding: '8px' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {a.bandcamp_url && (
                          <a href={a.bandcamp_url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '3px', background: 'rgba(29,185,84,0.15)', color: '#1db954', textDecoration: 'none', fontWeight: 700 }}>
                            BC
                          </a>
                        )}
                        {(a.spotify_url || extra.spotify) && (
                          <a href={a.spotify_url || extra.spotify} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '3px', background: 'rgba(29,185,84,0.15)', color: '#1ed760', textDecoration: 'none', fontWeight: 700 }}>
                            SP
                          </a>
                        )}
                        {a.soundcloud_url && (
                          <a href={a.soundcloud_url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '3px', background: 'rgba(255,85,0,0.15)', color: '#ff5500', textDecoration: 'none', fontWeight: 700 }}>
                            SC
                          </a>
                        )}
                        {(a.tidal_url || extra.tidal) && (
                          <a href={a.tidal_url || extra.tidal} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '3px', background: 'rgba(0,0,0,0.1)', color: '#000', textDecoration: 'none', fontWeight: 700 }}>
                            TDL
                          </a>
                        )}
                        {a.discogs_url && (
                          <a href={a.discogs_url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '3px', background: 'rgba(51,51,51,0.1)', color: '#333', textDecoration: 'none', fontWeight: 700 }}>
                            DC
                          </a>
                        )}
                        {a.instagram_url && (
                          <a href={a.instagram_url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '3px', background: 'rgba(225,48,108,0.15)', color: '#e1306c', textDecoration: 'none', fontWeight: 700 }}>
                            IG
                          </a>
                        )}
                        {a.youtube_url && (
                          <a href={a.youtube_url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '3px', background: 'rgba(255,0,0,0.15)', color: '#ff0000', textDecoration: 'none', fontWeight: 700 }}>
                            YT
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Detail Panel */}
          {detail && (
            <div style={{ width: '380px', flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
              <div className="entity-card" style={{ borderColor: 'var(--gold)', position: 'relative' }}>
                {/* Close button */}
                <button
                  onClick={() => { setDetail(null); setSelectedId(null); }}
                  style={{
                    position: 'absolute', top: '8px', right: '8px',
                    background: 'rgba(255,255,255,0.08)', border: '1px solid var(--border-color)',
                    borderRadius: '50%', width: '24px', height: '24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: 'var(--text-muted)', fontSize: '14px',
                    lineHeight: 1,
                  }}
                  title={t('common.close')}
                >×</button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', paddingRight: '28px' }}>
                  <div className="entity-name" style={{ fontSize: '18px' }}>{detail.name}</div>
                  {detail.validated === 'verified' && (
                    <span style={{
                      fontSize: '9px', padding: '2px 6px', borderRadius: '3px', fontWeight: 700,
                      background: 'rgba(76,175,80,0.15)', color: '#4caf50', textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>verified</span>
                  )}
                  {!!detail.has_sooncome && (
                    <span className="sooncome-badge" style={{ fontSize: '12px' }}>🔥 SOON COME</span>
                  )}
                </div>

                {/* Country + Genres (only show what we have) */}
                <div className="entity-meta" style={{ marginBottom: '8px' }}>
                  {[detail.country, (detail.genres || []).join(', ')].filter(Boolean).join(' · ') || ''}
                </div>

                {/* Bio */}
                {detail.bio && (
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.5' }}>
                    {detail.bio}
                  </p>
                )}

                {/* Music Links — including extra platforms */}
                {(() => {
                  const extra = getExtraUrls(detail.extra_urls);
                  return (
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                      {detail.bandcamp_url && <a href={detail.bandcamp_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#1db954', color: '#1db954' }}>Bandcamp</a>}
                      {(detail.spotify_url || extra.spotify) && <a href={detail.spotify_url || extra.spotify} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#1ed760', color: '#1ed760' }}>Spotify</a>}
                      {detail.soundcloud_url && <a href={detail.soundcloud_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#ff5500', color: '#ff5500' }}>SoundCloud</a>}
                      {(detail.tidal_url || extra.tidal) && <a href={detail.tidal_url || extra.tidal} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#000', color: '#000' }}>Tidal</a>}
                      {detail.discogs_url && <a href={detail.discogs_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#333', color: '#333' }}>Discogs</a>}
                      {(detail.deezer_url || extra.deezer) && <a href={detail.deezer_url || extra.deezer} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#a238ff', color: '#a238ff' }}>Deezer</a>}
                      {detail.youtube_url && <a href={detail.youtube_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#ff0000', color: '#ff0000' }}>YouTube</a>}
                      {detail.instagram_url && <a href={detail.instagram_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#e1306c', color: '#e1306c' }}>Instagram</a>}
                      {detail.facebook_url && <a href={detail.facebook_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm">👥 Facebook</a>}
                      {extra.tidal && <a href={extra.tidal} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#00ffff', color: '#00ffff' }}>🎶 Tidal</a>}
                      {extra.deezer && <a href={extra.deezer} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#ff0092', color: '#ff0092' }}>🎵 Deezer</a>}
                      {extra.apple_music && <a href={extra.apple_music} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#fc3c44', color: '#fc3c44' }}>🍎 Apple</a>}
                      {extra.qobuz && <a href={extra.qobuz} target="_blank" rel="noopener noreferrer" className="btn btn-sm">🎵 Qobuz</a>}
                      {extra.discogs && <a href={extra.discogs} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: '#888', color: '#888' }}>📀 Discogs</a>}
                      {detail.website_url && <a href={detail.website_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm">🌐 Web</a>}
                    </div>
                  );
                })()}

                {/* Upcoming Events only */}
                <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px', color: 'var(--gold)', letterSpacing: '1px', textTransform: 'uppercase' }}>
                  {t('artists.events_count')} ({upcomingEvents.length})
                </div>
                {upcomingEvents.length > 0 ? (
                  upcomingEvents.slice(0, 15).map((e: any) => (
                    <div key={e.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border-color)', fontSize: '12px' }}>
                      <div style={{ color: 'var(--green)', fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
                        {formatDate(e.date)}
                      </div>
                      <div style={{ fontWeight: 600 }}>{e.title}</div>
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>{t('artists.no_events')}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
